/* eslint-disable @typescript-eslint/naming-convention */
import React, { Dispatch, SetStateAction, FocusEvent } from 'react';

import { BoxProps, FormProps, TextFieldInputProps } from '@aircall/tractor';
import { ComponentSizes } from '@aircall/tractor/es/declarations/common.decl';

export type ValueType = number | string | boolean;
export type OnChangeType = (option: SelectSkeletonOption) => void;
export type OptionRenderer = (option: SelectSkeletonOption, isSelected: boolean) => React.ReactNode;

export interface SelectSkeletonOption {
  value: ValueType;
  label: string;
  group?: string;
}

export const SELECT_SKELETON_MENU_POSITIONS = {
  TOP: 'top',
  BOTTOM: 'bottom',
} as const;

export type SelectSkeletonMenuPosition =
  typeof SELECT_SKELETON_MENU_POSITIONS[keyof typeof SELECT_SKELETON_MENU_POSITIONS];

export interface SelectSkeletonProps extends BoxProps {
  /** Component to use for the input */
  inputComponent: SelectSkeletonInputComponent;

  /** Component to use for the dropdown menu */
  menuComponent?: SelectSkeletonMenuComponent;

  /** The selected option's value */
  defaultSelected?: ValueType;

  /** Hides the chevron */
  hideChevron?: boolean;

  /** A callback fired when selected value is changed */
  onChange: OnChangeType;

  onBlur?: (event: FocusEvent<HTMLElement>) => void;

  /** A callback fired when the menu is closed */
  onClose?: () => void;

  /** A list of key-value pair options containing the `value` and `label` attributes */
  options: SelectSkeletonOption[];

  /** The label of the field */
  label?: string;

  /** The name of the field */
  name: string;

  valueRenderer?: (selected?: SelectSkeletonOption) => string | undefined;

  /** The placeholder of input */
  placeholder?: string;

  /** Render an icon in the left side of input */
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;

  /** The size of component */
  size?: FormProps['size'];

  /** The background color of input and option item */
  backgroundColor?: string;

  /** A function to customize the rendering of option */
  optionRenderer?: OptionRenderer;

  /** Whether the input is disabled or not */
  disabled?: boolean;

  /** The position where we put `<SelectSkeletonMenu>` */
  menuPosition?: SelectSkeletonMenuPosition;

  /** Disable automatic focus on menu item */
  disableItemAutofocus?: true;

  /** An option to use react-dom's portal or not. Defaults to true.
   * Sparingly assign to false.
   */
  usePortal?: boolean;

  /** Override the maxHeight of the dropdown. Not from BoxProps because we also use this for the
   * min bottom offset
   */
  maxHeight?: number;

  /** The testing attribute */
  'data-test'?: string;
}

type SelectSkeletonComponentProps = Omit<
  SelectSkeletonProps,
  'menuComponent' | 'inputComponent' | keyof Omit<BoxProps, 'backgroundColor' | 'size'>
> & {
  isOpen: boolean;
  selectedOption: SelectSkeletonOption;
  menuPosition: SelectSkeletonMenuPosition;
};

export type SelectSkeletonInputComponentProps = Omit<
  SelectSkeletonComponentProps,
  'optionRenderer'
>;

export type SelectSkeletonInputComponent = React.ComponentType<SelectSkeletonInputComponentProps>;

export type SelectSkeletonMenuComponentProps = Omit<
  SelectSkeletonComponentProps,
  'valueRenderer' | 'icon'
> & {
  /** The original menu */
  children: React.ReactNode;
};

export type SelectSkeletonMenuComponent = React.ComponentType<SelectSkeletonMenuComponentProps>;

export interface ISelectSkeletonContext {
  selected?: SelectSkeletonOption;
  setSelected: Dispatch<SetStateAction<SelectSkeletonOption>>;
  toggleMenu: () => void;
  openMenu: () => void;
  closeMenu: () => void;
  onChange: OnChangeType;
  optionRenderer?: OptionRenderer;
  size?: FormProps['size'];
}

export interface SelectSkeletonContainerProps extends BoxProps {
  label?: string;
  name: string;
  children: React.ReactNode;
  disabled: boolean;
  menuPosition: SelectSkeletonMenuPosition;
  isOpen: boolean;
  hideChevron: boolean;
  size: ComponentSizes;
  backgroundColor: string;
  'data-test'?: string;
}

export interface SelectSkeletonMenuGeometry {
  width: number;
  top: number;
  left: number;
  menuPosition?: SelectSkeletonMenuPosition;
  maxHeight: number;
}

export type SelectSkeletonMenuProps = Omit<SelectSkeletonMenuComponentProps, 'children'> &
  SelectSkeletonMenuGeometry & {
    menuComponent: SelectSkeletonMenuComponent;
    disableItemAutofocus?: true;
    maxHeight: number;
  };

export interface SelectSkeletonItemProps {
  option: SelectSkeletonOption;
  index: number;
  disableAutofocus?: true;
  grouped: boolean;
  'data-test'?: string;
}

export interface SelectSkeletonInputFieldProps extends TextFieldInputProps {
  backgroundColor?: string;
}

export interface StyledSelectSkeletonItemProps extends Pick<SelectSkeletonItemProps, 'grouped'> {
  isActive: boolean;
  size?: ComponentSizes;
}
